// ─────────────────────────────────────────────────────────────────────────────
// ParkedPanel.tsx — THE PARKED COMPANIES WAITING ON THE OWNER'S DECISION.
//
// Owner ruling 2026-09-29: a panel for the parked companies, with Bring back through
// the existing unpark — history kept. The snag is the owner's reading (Parked
// Companies §5: "what the owner reads at seven in the morning"); the ones that need
// something upstream fixed come first. Bring back asks once more before it writes.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { MC } from '../missioncontrol/theme';
import { ParkedCompany, normalizeParked, unparkCompany } from '../../api/deltaParked';

export interface ParkedPanelProps {
  slot: any;
  refresh?: () => void;
  testId?: string;
}

const since = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

export default function ParkedPanel({ slot, refresh, testId = 'delta-parked' }: ParkedPanelProps) {
  const { phase, rows } = normalizeParked(slot);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [said, setSaid] = useState<Record<string, string | null>>({});
  const [back, setBack] = useState<Record<string, boolean>>({});

  const onBringBack = async (p: ParkedCompany) => {
    setConfirming(null);
    const out = await unparkCompany(p.companyId);
    setSaid((s) => ({ ...s, [p.companyId]: out.ok ? null : out.refused }));
    if (out.ok) {
      setBack((b) => ({ ...b, [p.companyId]: true }));
      if (refresh) refresh();
    }
  };

  const cell: React.CSSProperties = { padding: '8px 10px', borderBottom: `1px solid ${MC.line2}`, color: MC.text, fontSize: 13, verticalAlign: 'top' };
  const btn: React.CSSProperties = {
    background: MC.panel3, color: MC.teal, border: `1px solid ${MC.line}`, borderRadius: 10,
    padding: '4px 10px', fontSize: 11, letterSpacing: '.06em', cursor: 'pointer', marginRight: 6, whiteSpace: 'nowrap',
  };

  return (
    <section data-testid={testId} style={{
      background: MC.panel, borderRadius: MC.radius, boxShadow: MC.cardShadow, padding: '16px 18px', margin: '0 0 14px 0',
    }}>
      <div style={{ color: MC.text, fontSize: 15, marginBottom: 10 }}>
        Parked — waiting on your decision{phase === 'read' ? ` (${rows.length})` : ''}
      </div>
      {phase === 'unread' && <div data-testid={`${testId}-unread`} style={{ color: MC.dim, fontSize: 12 }}>not read</div>}
      {phase === 'read' && rows.length === 0 && (
        <div data-testid={`${testId}-empty`} style={{ color: MC.muted, fontSize: 12 }}>Nothing parked.</div>
      )}
      {phase === 'read' && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Company', 'Why it stopped', 'Run again?', 'Parked', ''].map((h) => (
              <th key={h} style={{ ...cell, color: MC.dim, fontSize: 11, textAlign: 'left', fontWeight: 400 }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.companyId} data-testid={`${testId}-row-${p.companyId}`}>
                <td style={cell}>{p.company}</td>
                <td style={{ ...cell, color: MC.muted, maxWidth: 520 }}>{p.snag}</td>
                <td style={{ ...cell, color: p.retryable === 'NO' ? MC.orange : MC.green }}
                    data-testid={`${testId}-retry-${p.companyId}`}>
                  {p.retryable === 'NO' ? 'fix first' : p.retryable === 'YES' ? 'can run' : '—'}
                </td>
                <td style={cell}>{since(p.parkedAt)}</td>
                <td style={cell}>
                  {back[p.companyId] ? (
                    <span data-testid={`${testId}-back-${p.companyId}`} style={{ color: MC.green, fontSize: 11 }}>brought back</span>
                  ) : confirming === p.companyId ? (
                    <>
                      <button type="button" style={btn} data-testid={`${testId}-confirm-${p.companyId}`}
                              onClick={() => onBringBack(p)}>CONFIRM</button>
                      <button type="button" style={btn} onClick={() => setConfirming(null)}>CANCEL</button>
                    </>
                  ) : (
                    <button type="button" style={btn} data-testid={`${testId}-bring-${p.companyId}`}
                            onClick={() => setConfirming(p.companyId)}>BRING BACK</button>
                  )}
                  {said[p.companyId] && <span data-testid={`${testId}-note-${p.companyId}`}
                                              style={{ color: MC.orange, fontSize: 11 }}>{said[p.companyId]}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
